import { useCallback, useMemo } from "react";

import queryString from "query-string";
import { useLocation } from "react-router-dom";

import { ProjectViewLinkInvitationSignUp } from "../components/ProjectViewLinkInvitationSignUp/ProjectViewLinkInvitationSignUp";
import SignUp from "../components/SignUp/SignUp";
import useNavigation from "../hooks/navigation";
import { useRegister } from "../hooks/useAuth";
import { useWindowSize, ViewportType } from "../hooks/useWindowSize";
import { SearchParam, SignupSource } from "../types";
import { PageSource } from "../types/tracking";
import { searchParamAsOptionalString } from "../urlUtils";
import { isEmailInvalid } from "../utils/validationUtils";

const SignUpContainer = () => {
  const location = useLocation();
  const navigation = useNavigation();
  const { viewportType } = useWindowSize();
  const isMobile = viewportType === ViewportType.Mobile;

  const queryParams = useMemo(() => queryString.parse(location.search), [location.search]);

  const appTrackingID = useMemo(
    () => searchParamAsOptionalString(queryParams[SearchParam.TrackingID]),
    [queryParams]
  );

  const {
    mutateAsync: mutateRegister,
    isPending: isRegisterPending,
    error: registerError,
  } = useRegister({ appTrackingID });

  const registerSuccessCallback = useCallback(() => {
    const isRedirecting = navigation.tryRedirectFromSearchParams(PageSource.Signup);
    if (isRedirecting) {
      return;
    }

    navigation.goToSubscription({
      additionalQueryParams: {
        [SearchParam.Source]: PageSource.Signup,
      },
    });
  }, [navigation]);

  const emailHint = useMemo(() => {
    const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
    const emailHint = hashParams.get(SearchParam.EmailHint);
    if (!emailHint || isEmailInvalid(emailHint)) {
      return null;
    }

    return emailHint;
  }, [location.hash]);

  const signupSource = useMemo(() => {
    const source = searchParamAsOptionalString(queryParams[SearchParam.Source]);
    const signupSourceParam = searchParamAsOptionalString(queryParams[SearchParam.SignupSource]);

    if (source === PageSource.AppAuth) {
      return SignupSource.AppSignup;
    }
    if (signupSourceParam === SignupSource.ProjectLink) {
      return SignupSource.ProjectLink;
    }
    if (emailHint) {
      return SignupSource.ReviewLink;
    }
    return SignupSource.WebSignup;
  }, [queryParams, emailHint]);

  const signUp = async (email: string, password: string, name: string, captcha?: string) => {
    try {
      await mutateRegister(
        { email, password, name, captcha, signupSource, isMobile },
        { onSuccess: registerSuccessCallback }
      );
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  if (emailHint) {
    return (
      <ProjectViewLinkInvitationSignUp
        submit={signUp}
        inProgress={isRegisterPending}
        signupError={registerError}
        emailHint={emailHint}
      />
    );
  }

  return <SignUp submit={signUp} inProgress={isRegisterPending} signupError={registerError} />;
};

export default SignUpContainer;
